'use client';

import { useTranslations } from 'next-intl';
import { ControlGrid } from '@/features/control/ui/ControlGrid';
import { ControlEditorModal } from '@/features/control/ui/ControlEditorModal';
import { hasValidControlFile } from '@/features/control/lib/control-validation';
import { CONTROL_LIMITS } from '@/features/control/constants';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { AlertTriangle } from 'lucide-react';
import type { Control, Angle } from '@/types/concept';
import type { RenderControl } from '@/types/render';

export interface StructureContentProps {
  // Processed controls from hook
  controls: Control[];
  selectedControl?: Control;
  hasSelectedControl: boolean;
  // Raw data
  selectedControlIds: Array<RenderControl | string>;
  angle?: Angle | null;
  /**
   * Backdrop image for the selected control card / preview / editor.
   * Caller computes from the active render-form mode:
   * angle photo in Explore, parent render image in Refine.
   * Falls back to the angle when null in Refine (e.g. no parent yet).
   */
  backdropUrl?: string | null;
  // Hook state
  isEditingControl: boolean;
  editingControlBase: Control | null;
  unusedControlsCount: number;
  deleteConfirmation: { control: Control; isUsed: boolean } | null;
  deleting: boolean;
  // Hook handlers
  onControlsChange: (controls: RenderControl[]) => void;
  handleCreateControl: () => void;
  handleEditControl: (forceNewVersion?: boolean) => void;
  handleEditControlFor: (control: Control, forceNewVersion?: boolean) => void;
  handleSaveEditedControl: (blob: Blob) => Promise<void>;
  handleDeleteControl: (control: Control) => void;
  handleConfirmDeleteControl: () => void;
  handleCancelDelete: () => void;
  handleControlToggle: (controlId: string) => void;
  cancelEditing: () => void;
  // Display options
  variant?: 'default' | 'compact';
}

export function StructureContent({
  controls,
  selectedControlIds,
  angle,
  backdropUrl,
  isEditingControl,
  editingControlBase,
  unusedControlsCount,
  deleteConfirmation,
  deleting,
  handleCreateControl,
  handleEditControlFor,
  handleSaveEditedControl,
  handleDeleteControl,
  handleConfirmDeleteControl,
  handleCancelDelete,
  handleControlToggle,
  cancelEditing,
}: StructureContentProps) {
  // Fall back to the angle photo when the caller didn't pass an explicit
  // backdrop (e.g. legacy callsites or Explore mode collapsed to undefined).
  const effectiveBackdropUrl =
    backdropUrl ?? angle?.attributes.file?.url ?? null;
  const t = useTranslations('render.settings.control');

  return (
    <>
      <div className="space-y-4">
        {/* 3-line hint — Apple parity (controls.hint.draw/labels/upload).
            One paragraph per sentence so wrapping stays predictable on narrow
            sheets. */}
        <div className="space-y-1">
          <p className="text-list-item-subtitle text-muted-foreground">
            {t('hintDraw')}
          </p>
          <p className="text-list-item-subtitle text-muted-foreground">
            {t('hintLabels')}
          </p>
          <p className="text-list-item-subtitle text-muted-foreground">
            {t('hintUpload')}
          </p>
        </div>

        {/* Single grid — leads with "Draw new" card, followed by existing
            custom controls. Replaces the prior split "Selected control" +
            "Available controls" layout (Apple has one grid). */}
        <ControlGrid
          controls={controls}
          selectedControlIds={selectedControlIds}
          backdropUrl={effectiveBackdropUrl}
          unusedControlsCount={unusedControlsCount}
          maxUnusedControls={CONTROL_LIMITS.MAX_UNUSED_CONTROLS}
          onControlToggle={handleControlToggle}
          onEditControl={(control) => handleEditControlFor(control, false)}
          onCopyAsNewControl={(control) =>
            handleEditControlFor(control, true)
          }
          onDeleteControl={handleDeleteControl}
          onCreateControl={handleCreateControl}
          hasValidFile={hasValidControlFile}
        />
      </div>

      {/* Control Editor Modal - Renders outside drawer */}
      {angle && (
        <ControlEditorModal
          isOpen={isEditingControl}
          // `file` is null for a CONSTRUCTED view (a camera, not a photo), and
          // the two-column inspector mounts this sheet's content even while it
          // is closed — so an unguarded `.file.url` crashed the whole render
          // panel there. The editor already accepts a null backdrop (a sketch
          // on a clean canvas), which is exactly right when there is no photo.
          angleImageUrl={effectiveBackdropUrl ?? angle.attributes.file?.url ?? null}
          initialControlUrl={editingControlBase?.attributes.file?.url}
          onSave={handleSaveEditedControl}
          onCancel={cancelEditing}
        />
      )}

      {/* Delete Control Confirmation Modal */}
      <AlertDialog
        open={deleteConfirmation !== null}
        onOpenChange={handleCancelDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <AlertDialogTitle>{t('deleteDialogTitle')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-2">
              <span>{t('deleteDialogDescription')}</span>
              {deleteConfirmation?.isUsed && (
                <span className="block text-list-item-subtitle">
                  {t('deleteDialogWarning')}
                </span>
              )}
              <span className="block text-list-item-subtitle">
                {t('deleteDialogWarningPermanent')}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('deleteDialogCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteControl}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t('deleteDialogDeleting') : t('deleteDialogConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
